import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FaArrowRight } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';


const PaymentCalculator = ({ initialAmount, readOnlyAmount = false }) => {
    const navigate = useNavigate();
    const [amount, setAmount] = useState(initialAmount || 100000);
    const [term, setTerm] = useState(36); // Default to 36 months
    const [formattedAmount, setFormattedAmount] = useState('');
    const [isAmountInvalid, setIsAmountInvalid] = useState(false);
    const [monthlyPayments, setMonthlyPayments] = useState({
        12: 0,
        18: 0,
        24: 0,
        36: 0
    });
    const [paymentType, setPaymentType] = useState('standard'); // 'standard' or 'deferred'

    // Format sayıyı binlik ayırıcı ile formatlama
    const formatNumber = (num) => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    // Component yüklendiğinde ve amount değiştiğinde
    useEffect(() => {
        // Tutarı formatlı göster
        setFormattedAmount(formatNumber(amount));

        // Tutar geçerli mi kontrol et
        setIsAmountInvalid(amount > 2000000);

        // Aylık ödemeleri hesapla
        if (amount > 0) {
            const payments = {
                12: calculatePaymentForTerm(amount, 12),
                18: calculatePaymentForTerm(amount, 18),
                24: calculatePaymentForTerm(amount, 24),
                36: calculatePaymentForTerm(amount, 36)
            };
            setMonthlyPayments(payments);
        }
    }, [amount, paymentType]);

    // Formatlı tutarı değiştirince amount state'ini güncelle
    const handleFormattedAmountChange = (e) => {
        const value = e.target.value.replace(/\./g, ''); // Noktalı kısımları temizle
        const numericValue = value.replace(/\D/g, ''); // Sadece rakamları al

        if (numericValue) {
            const parsedValue = parseInt(numericValue, 10);
            setAmount(parsedValue);
        } else {
            setAmount(0);
        }
    };

    // Verilen vade için aylık ödeme hesaplama
    // Verilen vade için aylık ödeme hesaplama
    const calculatePaymentForTerm = (loanAmount, months) => {
        const interestRates = {
            12: 0.055,
            18: 0.055,
            24: 0.055,
            36: 0.055
        };

        const rate = interestRates[months] || 0.01;
        let finalLoanAmount = loanAmount;

        // İleri vadeli taksit seçiliyse 90 gün erteleme faizi ekle
        if (paymentType === 'deferred') {
            const dailyInterestRate = rate / 30;
            const deferredInterest = loanAmount * dailyInterestRate * 90; // 90 gün sabit
            finalLoanAmount = loanAmount + deferredInterest;
        }

        const monthlyPayment = (finalLoanAmount * (rate * Math.pow((1 + rate), months))) / (Math.pow((1 + rate), months) - 1);
        return monthlyPayment.toFixed(0);
    };

    // Başvuru sayfasına yönlendir
    const handleApplyNow = (e) => {
        e.preventDefault();

        if (isAmountInvalid) return; // Geçersiz tutar varsa işlemi durdur

        navigate('/application', {
            state: {
                vehicleAmount: amount,
                monthlyPayment: monthlyPayments[term],
                term: term
            }
        });
    };

    // Tutar aralığına göre gösterilecek vade seçeneklerini belirle
    const getAvailableTerms = () => {
        if (amount > 1290000 && amount <= 2000000) {
            return [12, 18, 24]; // 1.290.000 ile 2.000.000 arası
        } else if (amount <= 1290000) {
            return [12, 18, 24, 36]; // 1.290.000'dan küçük
        }
        return []; // Geçersiz tutar için boş dizi
    };

    // Seçilebilir vadeleri al
    const availableTerms = getAvailableTerms();

    // Geçerli vade seçili değilse, ilk geçerli vadeyi seç
    useEffect(() => {
        if (availableTerms.length > 0 && !availableTerms.includes(term)) {
            setTerm(availableTerms[0]);
        }
    }, [availableTerms, term]);

    return (
        <div className="calculator-content">
            <Form onSubmit={handleApplyNow}>
                <Form.Group className="mb-3">
                    <Form.Label>Peşinat Miktarını Girin</Form.Label>
                    <div className="input-group">
                        <span className="input-group-text">₺</span>
                        <Form.Control
                            type="text"
                            value={formattedAmount}
                            onChange={handleFormattedAmountChange}
                            className={isAmountInvalid ? "is-invalid" : ""}
                            readOnly={readOnlyAmount}
                        />
                    </div>
                    {isAmountInvalid && (
                        <div className="invalid-feedback d-block">
                            2.000.000 TL'den fazla kredi verilemiyor.
                        </div>
                    )}
                </Form.Group>


               < Form.Group className="mb-3">
                <Form.Label>Ödeme Seçeneği</Form.Label>
                <div className="d-flex gap-4 mt-2">
                    <Form.Check
                        type="radio"
                        id="payment-standard"
                        name="paymentType"
                        label="Standart Taksit"
                        checked={paymentType === 'standard'}
                        onChange={() => setPaymentType('standard')}
                    />
                    <Form.Check
                        type="radio"
                        id="payment-deferred"
                        name="paymentType"
                        label="İleri Vadeli Taksit (+90 gün)"
                        checked={paymentType === 'deferred'}
                        onChange={() => setPaymentType('deferred')}
                    />
                </div>
                <Form.Text className="text-muted">
                    {paymentType === 'deferred' ? 'İlk ödeme 120 gün sonra başlar (90 gün erteleme)' : 'İlk ödeme 30 gün sonra başlar'}
                </Form.Text>
            </Form.Group>


                {!isAmountInvalid && (
                    <Form.Group className="mb-4">
                        <Form.Label>Vade Sayısını Seçin</Form.Label>
                        <div className="term-options-compact">
                            {availableTerms.map(months => (
                                <div
                                    key={months}
                                    className={`term-option-compact ${term === months ? 'active' : ''}`}
                                    onClick={() => setTerm(months)}
                                >
                                    <div className="term-month">{months} Ay</div>
                                    <div className="term-price">₺ {formatNumber(monthlyPayments[months])}</div>
                                </div>
                            ))}
                        </div>
                    </Form.Group>
                )}

                <div className="mt-4">
                    <div className="result-box mb-4 text-center">
                        <h2 className="amount">₺ {!isAmountInvalid ? formatNumber(monthlyPayments[term]) : "---"}</h2>
                        <small className="text-muted">
                            <div className="info-notice mb-4">
                                <p className="info-text">
                                     Tahmini hesaplama sonucudur. Detaylı ödeme planınız başvuru sonrası oluşturulacaktır.
                                </p>
                            </div>
                        </small>
                    </div>

                    <div className="d-grid gap-2">
                        <Button
                            variant="dark"
                            type="submit"
                            size="lg"
                            className="apply-btn-black"
                            disabled={isAmountInvalid}
                        >
                            Hemen Başvur
                        </Button>
                    </div>
                </div>
            </Form>
        </div>
    );
};

export default PaymentCalculator;